import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts';

// Import MUI Components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ThemeToggle from '../common/ThemeToggle';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const canUseBuilderLocator = ['admin', 'broker', 'loan_officer'].includes(user?.role);

  // --- 1. SEPARATE STATE FOR EACH MENU ---
  const [extrasAnchorEl, setExtrasAnchorEl] = useState(null);
  const [adminAnchorEl, setAdminAnchorEl] = useState(null);
  const [notificationsSubAnchorEl, setNotificationsSubAnchorEl] = useState(null);

  const isExtrasOpen = Boolean(extrasAnchorEl);
  const isAdminOpen = Boolean(adminAnchorEl);
  const isNotificationsSubOpen = Boolean(notificationsSubAnchorEl);

  // --- 2. SEPARATE HANDLERS ---
  const handleExtrasClick = (event) => {
    setExtrasAnchorEl(event.currentTarget);
  };

  const handleExtrasClose = () => {
    setExtrasAnchorEl(null);
  };

  const handleAdminClick = (event) => {
    setAdminAnchorEl(event.currentTarget);
  };

  const handleAdminClose = () => {
    setAdminAnchorEl(null);
    setNotificationsSubAnchorEl(null); // Ensure submenu closes when main menu closes
  };

  const handleNotificationsSubClick = (event) => {
    setNotificationsSubAnchorEl(event.currentTarget);
  };

  const handleNotificationsSubClose = () => {
    setNotificationsSubAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Custom component for MUI buttons acting as react-router-dom Links
  const LinkButton = ({ to, children, ...props }) => (
    <Button
      component={Link}
      to={to}
      sx={{
        color: 'text.primary',
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none',
        px: 2,
        py: 1,
        borderRadius: '8px',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'action.hover',
          color: 'text.primary',
        },
      }}
      {...props}
    >
      {children}
    </Button>
  );

  return (
    <AppBar
      position="sticky"
      data-morty-sticky-nav="primary"
      elevation={0}
      sx={{
        top: 0,
        backgroundColor: 'var(--morty-nav-background)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid',
        borderColor: 'divider',
        boxShadow: 'var(--morty-nav-shadow)',
        zIndex: 1100,
      }}
    >
      <Toolbar sx={{
        justifyContent: 'space-between',
        px: { xs: 2, md: 4 },
        py: 1,
        minHeight: '64px !important',
      }}>
        {/* Logo/Title */}
        <Typography
          variant="h5"
          component={Link}
          to="/"
          sx={{
            textDecoration: 'none',
            color: 'text.primary',
            fontFamily: '"Frank Ruhl Libre", serif',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            fontSize: '1.5rem',
            transition: 'opacity 0.2s ease',
            '&:hover': {
              opacity: 0.7,
            }
          }}
        >
          Morty
        </Typography>

        {/* Navigation Links */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
          <LinkButton to="/dashboard">Dashboard</LinkButton>
          <LinkButton to="/document-management">Document Management</LinkButton>
          <LinkButton to="/refinance-opportunities">Refinances</LinkButton>

          {/* Admin Dropdown */}
          <Button
            id="admin-button"
            aria-controls={isAdminOpen ? 'admin-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isAdminOpen ? 'true' : undefined}
            onClick={handleAdminClick}
            sx={{
              color: 'text.primary',
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              px: 2,
              py: 1,
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: '1.2rem', opacity: 0.6 }} />}
          >
            Admin
          </Button>
          <Menu
            id="admin-menu"
            anchorEl={adminAnchorEl}
            open={isAdminOpen}
            onClose={handleAdminClose}
            MenuListProps={{ 'aria-labelledby': 'admin-button' }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'var(--morty-menu-background)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'var(--morty-menu-shadow)',
                mt: 1,
                minWidth: '180px',
              },
              '& .MuiMenuItem-root': {
                fontSize: '0.875rem',
                py: 1.5,
                px: 2.5,
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          >
            <MenuItem onClick={() => { handleAdminClose(); navigate('/document-types'); }}>
              Document Types
            </MenuItem>
            <MenuItem onClick={() => { handleAdminClose(); navigate('/rolodex'); }}>
              Rolodex
            </MenuItem>
            {canUseBuilderLocator && (
              <MenuItem onClick={() => { handleAdminClose(); navigate('/builder-locator'); }}>
                Builder Locator
              </MenuItem>
            )}
            <MenuItem onClick={() => { handleExtrasClose(); navigate('/all-submissions'); }}>
              All Documents
            </MenuItem>
            <MenuItem onClick={() => { handleExtrasClose(); navigate('/field-analysis'); }}>
              Field Analysis
            </MenuItem>
            <MenuItem onClick={() => { handleAdminClose(); navigate('/admin/docuseal-tester'); }}>
              DocuSeal Templates
            </MenuItem>
            <MenuItem onClick={() => { handleAdminClose(); navigate('/admin/plaid-tester'); }}>
              Plaid Portal 🏦
            </MenuItem>

            {/* Notifications Submenu Trigger */}
            <MenuItem
              onClick={handleNotificationsSubClick}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              Notifications
              <ChevronRightIcon sx={{ fontSize: '1.2rem', opacity: 0.6, ml: 1 }} />
            </MenuItem>

            <MenuItem onClick={() => { handleAdminClose(); navigate('/plants'); }}>
              Plants & Harvests 🌿
            </MenuItem>

            {user && user.role === 'admin' && (
              <>
                <MenuItem onClick={() => { handleAdminClose(); navigate('/users'); }}>
                  Users
                </MenuItem>
                <MenuItem onClick={() => { handleAdminClose(); navigate('/crons'); }}>
                  Crons
                </MenuItem>
              </>
            )}
          </Menu>

          {/* Notifications Submenu */}
          <Menu
            id="notifications-submenu"
            anchorEl={notificationsSubAnchorEl}
            open={isNotificationsSubOpen}
            onClose={handleNotificationsSubClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: 'var(--morty-menu-background)',
                backdropFilter: 'blur(20px) saturate(180%)',
                WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'var(--morty-menu-shadow)',
                minWidth: '180px',
              },
              '& .MuiMenuItem-root': {
                fontSize: '0.875rem',
                py: 1.5,
                px: 2.5,
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}
          >
            <MenuItem onClick={() => { handleNotificationsSubClose(); handleAdminClose(); navigate('/admin/email-tester'); }}>
              Email Templates
            </MenuItem>
            <MenuItem onClick={() => { handleNotificationsSubClose(); handleAdminClose(); navigate('/email-activity'); }}>
              Email Activity
            </MenuItem>
            <MenuItem onClick={() => { handleNotificationsSubClose(); handleAdminClose(); navigate('/approval-email-review'); }}>
              Approval Emails
            </MenuItem>
            <MenuItem onClick={() => { handleNotificationsSubClose(); handleAdminClose(); navigate('/loan-email-review'); }}>
              Loan Emails
            </MenuItem>
            <MenuItem onClick={() => { handleNotificationsSubClose(); handleAdminClose(); navigate('/loan-email-templates'); }}>
              Email Classification
            </MenuItem>
          </Menu>

        </Box>

        {/* User Info and Logout */}
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                fontSize: '0.875rem',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {user.full_name}
            </Typography>
            <ThemeToggle />
            <Button
              onClick={handleLogout}
              sx={{
                color: 'text.primary',
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none',
                px: 2,
                py: 0.75,
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderColor: 'text.disabled',
                },
              }}
            >
              Logout
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

export default Navbar;
